import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { IOrgContextObj } from "../types/index";
import { errorAlert, makeApiRequest } from "../util";
import AuthContext from "./authContext";

const OrgContext = React.createContext<IOrgContextObj>({
  orgs: [],
  setOrgs: null,
  selectedOrg: null,
  updateOrg: null,
  selectedTranslationType: null,
  updateTranslationType: null,
  setSearchTerm: null,
  isLoading: false,
  noSearchResult: false,
  handleRefreshOrgList: null,
});

export const OrgProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { token, scopes } = useContext(AuthContext);
  const org = localStorage.getItem("org");
  const translationType = localStorage.getItem("translationType");

  const [orgs, setOrgs] = useState<
    Array<{
      id: string;
      text: string;
    }>
  >([]);
  const loadedPageRef = useRef(new Set()); // this will keep count of page param sent in API
  const abortController = useRef<AbortController>(); // to avoid multiple fetch request for searched term
  const [isLoading, setIsLoading] = useState(false);
  const [noSearchResult, setNoSearchResult] = useState(false);
  const [numberOfSearchedPages, setNumberOfSearchedPages] = useState(0); // to hold count of total results from search API
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");

  // Constants for optimization
  const PAGE_SIZE = 100;
  // const BATCH_SIZE = 1; // Load 1 pages at a time
  const MIN_SEARCH_CHARS = 1;
  let MAX_BATCHES = numberOfSearchedPages; // Total Number of Pages (ex-2 pages total = 200 orgs)
  const [selectedOrg, setSelectedOrg] = useState<{
    id: string;
    text: string;
  } | null>(org ? JSON.parse(org) : null);

  const [selectedTranslationType, setSelectedTranslationType] = useState<{
    id: string;
    text: string;
  } | null>(translationType ? JSON.parse(translationType) : null);

  const fetchOrganizations = async (token: string, page: number) => {
    let filteredOrg: {
      id: string;
      text: string;
    }[] = [];
    try {
      const response = await makeApiRequest<{
        Rows: Array<{
          "ORG-ID": string;
          "ORG-NAME": string;
        }>;
        NbrOfRows: number;
        NbrOfPages: number;
      }>({
        path: `/api/v1/organisations?page=${page}&pageSize=${PAGE_SIZE}`,
        token,
      });

      filteredOrg = response.Rows.map((row) => ({
        id: row["ORG-ID"],
        text: `${row["ORG-NAME"]} (${row["ORG-ID"]})`,
      }));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
      setOrgs([]);
    }
    return filteredOrg;
  };

  const fetchOrganizationsBySearch = async (
    text: string,
    page?: number,
    signal?: any
  ) => {
    if (page) setIsLoading(true);
    let allMatchesArray = [];
    let totalRows = 0;
    try {
      const queryParams = new URLSearchParams({
        "ORG-NAME": `${text}`,
        pageSize: "100",
        page: `${page ?? "1"}`,
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await makeApiRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Rows: Array<any>;
        NbrOfRows: number;
        NbrOfPages: number;
      }>({
        path: `/api/v1/organisations?${queryParams}`,
        token,
        signal,
      });
      if (!page) {
        MAX_BATCHES = response.NbrOfPages;
        setNumberOfSearchedPages(response.NbrOfPages);
      }
      allMatchesArray = response.Rows;
      totalRows = response.NbrOfRows;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setIsLoading(false);
    }
    return { allMatchesArray, totalRows };
  };

  // Load first 100 organisations
  const initializeDropdown = async () => {
    let initialResult: {
      id: string;
      text: string;
    }[] = [];
    if (loadedPageRef.current.has(0)) {
      return null; // Already loaded first page
    }

    if (token) {
      setIsLoading(true);
      try {
        initialResult = await fetchOrganizations(token, 1);
        setOrgs([...initialResult]);
        loadedPageRef.current.add(0);
      } catch (error: any) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
    return initialResult;
  };

  // Function to filter objects by unique ID
  function getObjectsWithUniqueIds(array: any[]) {
    const seen = new Map();

    return array.filter((item) => {
      // Skip if we've seen this ID before
      if (seen.has(item.id)) {
        return false;
      }

      // Mark this ID as seen and keep the item
      seen.set(item.id, true);
      return true;
    });
  }

  const loadMoreData = async (term: string, signal?: any) => {
    let allOrgs = [...orgs];
    const allMatches = allOrgs.filter((org) =>
      org.text.toLowerCase().includes(term)
    );

    const newOrgs = [];
    // Load batches until we find enough matches or hit our limit
    for (let i = 1; i <= MAX_BATCHES; i += 1) {
      // Check if signal is aborted
      if (signal.aborted) {
        throw new Error("Search cancelled");
      }
      try {
        if (token) {
          // eslint-disable-next-line no-await-in-loop
          const { allMatchesArray } = await fetchOrganizationsBySearch(
            term,
            i,
            signal
          );
          if (allMatchesArray.length === 0) {
            // We've loaded all organizations
            break;
          }
          const filteredOrg = allMatchesArray.map((row) => ({
            id: row["ORG-ID"],
            text: `${row["ORG-NAME"]} (${row["ORG-ID"]})`,
          }));
          newOrgs.push(...filteredOrg);
          loadedPageRef.current.add(i);
        }
      } catch (error) {
        console.error(`Failed to load page ${i}:`, error);
      }
    }
    // Add new orgs to our collection
    allOrgs = [...allOrgs, ...newOrgs];
    const uniqueObjects = getObjectsWithUniqueIds(allOrgs);

    // Update state with what we have so far
    setOrgs([...uniqueObjects]);
    return allMatches;
  };

  // Handle search input changes
  const handleSearch = async (term: string) => {
    // Reset if search is cleared
    if (!term) {
      setOrgs(orgs.slice(0, 100));
      return;
    }

    if (term.length >= MIN_SEARCH_CHARS) {
      // Cancel any previous search in progress
      if (abortController.current) {
        abortController.current.abort();
      }
      // Create new abort controller for this search
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      // Filter current organizations first
      const lowerTerm = term.toLowerCase();
      const matches1 = orgs.filter((org) =>
        org.text.toLowerCase().includes(lowerTerm)
      );
      const { totalRows } = await fetchOrganizationsBySearch(term);
      // If we haven't had enough matches
      if (totalRows && totalRows > matches1.length) {
        setIsLoading(true);
        try {
          await loadMoreData(term, signal);
          if (signal.aborted) return;
        } catch (error) {
          console.error(error);
        } finally {
          if (!signal.aborted) {
            setIsLoading(false);
          }
        }
      }
      if (
        totalRows === 0 &&
        term.length >= 1 &&
        !term.includes("All Organisation")
      ) {
        setNoSearchResult(true);
      }
    }
  };

  const updateOrg = (newOrg: { id: string; text: string } | null) => {
    setSelectedOrg(newOrg);
    if (newOrg) {
      localStorage.setItem("org", JSON.stringify(newOrg));
    } else {
      setSelectedTranslationType(null);
      localStorage.removeItem("org");
      localStorage.removeItem("translationType");
    }
  };

  const handleRefreshOrgList = (
    updatedOrg: { id: string; text: string } | null
  ) => {
    if (updatedOrg) {
      setOrgs((prevOrgs) =>
        prevOrgs.map((org) =>
          org.id === updatedOrg.id ? { ...org, ...updatedOrg } : org
        )
      );
    }
  };

  const updateTranslationType = (
    newTranslationType: { id: string; text: string } | null
  ) => {
    if (newTranslationType) {
      localStorage.setItem(
        "translationType",
        JSON.stringify(newTranslationType)
      );
      setSelectedTranslationType(newTranslationType);
    } else {
      localStorage.removeItem("translationType");
      setSelectedTranslationType(null);
    }
  };

  useEffect(() => {
    if (token && scopes && scopes.orgs && scopes.orgs.includes("r")) {
      if (debouncedTerm) {
        handleSearch(searchTerm);
      }
      initializeDropdown();
    }
  }, [token, scopes, debouncedTerm]);

  // Debounce function - waits until user stops typing before making API call
  useEffect(() => {
    setNoSearchResult(false);
    // Set a timer to update the debounced value after 600ms
    const timerId = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 600);

    // Clean up function will run if searchTerm changes before timeout completes
    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const orgValue = useMemo(
    () => ({
      orgs,
      setOrgs,
      selectedOrg,
      updateOrg,
      selectedTranslationType,
      updateTranslationType,
      handleSearch,
      setSearchTerm,
      isLoading,
      noSearchResult,
      handleRefreshOrgList,
    }),
    [orgs, selectedOrg, selectedTranslationType, isLoading, noSearchResult]
  );

  return <OrgContext.Provider value={orgValue}>{children}</OrgContext.Provider>;
};

export default OrgContext;
